import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    Skeleton,
    useTheme,
    useMediaQuery,
    Fab
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Receipt as InvoiceIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { purchaseOrdersAPI } from '../api/api';
import { formatCurrency, formatDate, LABELS } from '../config';
import AdvancedFilters from './common/AdvancedFilters';
import { useAuth } from '../contexts/AuthContext';

const PurchaseOrders = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const { user } = useAuth();

    const [pos, setPOs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        status: '',
        search: '',
        sortBy: 'po_date',
        sortOrder: 'DESC'
    });

    useEffect(() => {
        fetchPOs();
    }, [filters]);

    const fetchPOs = async () => {
        try {
            setLoading(true);
            const response = await purchaseOrdersAPI.getAll(filters);
            if (response.data.success) {
                setPOs(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching POs:', error);
            toast.error('Failed to load purchase orders');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Delete Purchase Order?',
            text: 'This cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Delete'
        });

        if (result.isConfirmed) {
            try {
                await purchaseOrdersAPI.delete(id);
                toast.success('PO deleted');
                fetchPOs();
            } catch (error) {
                toast.error(error.response?.data?.message || 'Failed to delete PO');
            }
        }
    };

    const handleConvertToInvoice = async (po) => {
        const result = await Swal.fire({
            title: 'Convert to Invoice?',
            text: `This will create an invoice from PO ${po.po_number}`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Convert'
        });

        if (result.isConfirmed) {
            try {
                const response = await purchaseOrdersAPI.convertToInvoice(po.id, {});
                toast.success(`Invoice ${response.data.data.invoice_number} created!`);
                navigate(`/invoices/${response.data.data.invoice_id}`);
            } catch (error) {
                toast.error('Failed to convert PO to invoice');
            }
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'DRAFT': return 'default';
            case 'CONFIRMED': return 'info';
            case 'FULFILLED': return 'success';
            case 'CANCELLED': return 'error';
            default: return 'default';
        }
    };

    // Mobile Card View
    const POCard = ({ po }) => (
        <Card sx={{ mb: 2 }} className="hover-lift">
            <CardContent sx={{ pb: '12px !important' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box>
                        <Typography variant="subtitle2" color="secondary" fontWeight={600}>
                            {po.po_number}
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {po.vendor_name || po.customer_name}
                        </Typography>
                    </Box>
                    <Chip
                        label={LABELS.poStatus[po.status]}
                        size="small"
                        color={getStatusColor(po.status)}
                    />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Date</Typography>
                        <Typography variant="body2">{formatDate(po.po_date)}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" color="text.secondary">Total</Typography>
                        <Typography variant="body2" fontWeight={600}>{formatCurrency(po.total_amount)}</Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 1 }}>
                    {user && user.role !== 'VIEWER' && po.status !== 'FULFILLED' && po.status !== 'CANCELLED' && (
                        <IconButton size="small" color="primary" onClick={() => handleConvertToInvoice(po)}>
                            <InvoiceIcon fontSize="small" />
                        </IconButton>
                    )}
                    {user && user.role !== 'VIEWER' && (
                        <>
                            <IconButton size="small" onClick={() => navigate(`/purchase-orders/${po.id}`)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDelete(po.id)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </>
                    )}
                </Box>
            </CardContent>
        </Card>
    );

    return (
        <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" fontWeight={700}>
                    Purchase Orders
                </Typography>
                {!isMobile && user && user.role !== 'VIEWER' && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/purchase-orders/new')}
                    >
                        New PO
                    </Button>
                )}
            </Box>

            <AdvancedFilters
                filters={filters}
                onFilterChange={setFilters}
                configs={{
                    search: true,
                    dateRange: true,
                    status: true,
                    sortBy: [
                        { label: 'Date', value: 'po_date' },
                        { label: 'Amount', value: 'total_amount' },
                        { label: 'PO #', value: 'po_number' },
                        { label: 'Delivery Date', value: 'expected_delivery' }
                    ]
                }}
                data={{
                    statuses: [
                        { label: 'Draft', value: 'DRAFT' },
                        { label: 'Confirmed', value: 'CONFIRMED' },
                        { label: 'Fulfilled', value: 'FULFILLED' },
                        { label: 'Cancelled', value: 'CANCELLED' }
                    ]
                }}
            />

            {/* Content */}
            {loading ? (
                [...Array(5)].map((_, i) => <Skeleton key={i} height={100} sx={{ mb: 1 }} />)
            ) : isMobile ? (
                <Box>
                    {pos.map((po) => (
                        <POCard key={po.id} po={po} />
                    ))}
                    {pos.length === 0 && (
                        <Card>
                            <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                <Typography color="text.secondary">No purchase orders yet</Typography>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            ) : (
                <Card>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>PO #</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Vendor</TableCell>
                                    <TableCell>Expected Delivery</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                    <TableCell align="center">Status</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pos.map((po) => (
                                    <TableRow key={po.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600} color="secondary">
                                                {po.po_number}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{formatDate(po.po_date)}</TableCell>
                                        <TableCell>
                                            <Typography fontWeight={500}>{po.vendor_name || po.customer_name}</Typography>
                                        </TableCell>
                                        <TableCell>{formatDate(po.expected_delivery) || '-'}</TableCell>
                                        <TableCell align="right">{formatCurrency(po.total_amount)}</TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={LABELS.poStatus[po.status]}
                                                size="small"
                                                color={getStatusColor(po.status)}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            {user && user.role !== 'VIEWER' && (
                                                <>
                                                    {po.status !== 'FULFILLED' && po.status !== 'CANCELLED' && (
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleConvertToInvoice(po)}
                                                            title="Convert to Invoice"
                                                        >
                                                            <InvoiceIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                    <IconButton size="small" onClick={() => navigate(`/purchase-orders/${po.id}`)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" color="error" onClick={() => handleDelete(po.id)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {pos.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                            <Typography color="text.secondary">No purchase orders yet</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            )}

            {/* Mobile FAB */}
            {isMobile && user && user.role !== 'VIEWER' && (
                <Fab
                    color="primary"
                    sx={{ position: 'fixed', bottom: 72, right: 16 }}
                    onClick={() => navigate('/purchase-orders/new')}
                >
                    <AddIcon />
                </Fab>
            )}
        </Box>
    );
};

export default PurchaseOrders;
